import { RuleDataForm, RuleItem, RuleStatuses, RuleTransitions } from 'models/rules';
import { sdk, API_FUNCTIONS, checkContainsId, checkIfGreatSuccess, RULES_ENDPOINTS, queryKeys } from 'utils/query';
import { useTranslation } from 'react-i18next';
import { useQueryClient, QueryKey } from '@tanstack/react-query';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { useDrawerContext } from 'context/hooks';
import { ApiTaskResult } from 'models/general';
import { useCustomMutation } from '../useCustomMutation';
import { getTriggerData } from './useCreateRule';

const editRule = async (data: RuleDataForm) => {
  if (!data.groupId) throw new EmptyParametersError();

  const triggerData = getTriggerData(data);

  const postData = {
    type: data.trigger,
    notification: {
      title: data.name,
      description: data.description,
      priority: Number(data.priority),
    },
    parameters: {
      frequency: data.frequency,
      ...triggerData,
    },
    targets: data.patientId ? { patientIds: [data.patientId] } : { groupIds: [data.groupId] },
    recipients: { groupIds: [data.groupId] },
  };

  const result = await sdk.tasks.api.put<RuleItem, unknown>(
    API_FUNCTIONS.monitoring,
    `${RULES_ENDPOINTS.monitoringTemplates}/${data.id}`,
    postData,
    {},
  );

  const checkResult = checkContainsId({ result }) && result.id === data.id;

  if (checkResult && data.toStatus === RuleStatuses.inReview) {
    // after creation put rule in-review
    const resultSubmit = await sdk.tasks.api.post<ApiTaskResult, unknown>(
      API_FUNCTIONS.monitoring,
      `${RULES_ENDPOINTS.monitoringRules}/${result.id}/${RuleTransitions.submit}`,
      {},
      {},
    );
    return checkIfGreatSuccess({ result: resultSubmit });
  }

  return checkResult;
};

interface EditRuleProps {
  reset: () => void;
}

export const useEditRule = ({ reset }: EditRuleProps) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const queryClient = useQueryClient();
  return useCustomMutation({
    fn: editRule,
    onSuccess: {
      message: t('success_data_updated', { value: t('alerts_rule_label') }),
      callback: async (_result, variables) => {
        const { groupId, patientId, id, toStatus } = variables;

        const queryKey = patientId
          ? [`${queryKeys.patientRules}-${patientId}-${groupId}`]
          : [`${queryKeys.groupRules}-${groupId}`];

        const keys: QueryKey[] = [[queryKeys.ruleData, id], queryKey];
        if (toStatus !== RuleStatuses.inReview) {
          closeDrawer();
        }
        await Promise.all(
          keys.map(async (key) => {
            await queryClient.invalidateQueries({ queryKey: key });
          }),
        );
      },
    },
    onError: {
      reset,
    },
  });
};
